import React, { useState, useEffect, useCallback, useRef } from 'react';
import { confirmar } from '../components/confirmacao';
import apiService from '../services/apiService';
import './WhitelabelConfig.css';

// Resolve o user_id (role admin) do prop ou do localStorage.
function resolveUserId(user) {
  if (user?.user_id) return user.user_id;
  try {
    const a = JSON.parse(localStorage.getItem('aliado_auth') || '{}');
    return a?.user?.user_id;
  } catch (_) {
    return undefined;
  }
}

// Decide o tipo de input pra um campo do tema com base no VALOR (robusto a qualquer
// nome de chave: cores hex -> color picker; números -> number; resto (rgba, fontes,
// textos) -> texto).
function temaFieldKind(key, value) {
  if (typeof value === 'number') return 'number';
  if (typeof value === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value.trim())) return 'color';
  return 'text';
}

const TARGET_DEFAULT = '__default__';

/**
 * `scope` é OPCIONAL e vem do ScopeProvider quando esta tela roda como aba de Configurações (§9.3).
 * Sem ele — na rota antiga /whitelabel-config — a tela mantém o próprio seletor e é a de antes.
 *
 * O modelo desta tela é diferente das outras duas abas, e isso aparece na UI: whitelabel tem
 * DEFAULT GLOBAL -> EMPRESA -> FASE, e **não tem nível de loja**. Escopo de loja, portanto, não
 * configura nada aqui — a tela diz isso em vez de mostrar campos que não gravariam em lugar nenhum.
 */
export default function WhitelabelConfig({ user, scope }) {
  const userId = resolveUserId(user);
  const escopoExterno = !!scope?.subdomain;
  const escopoFase = escopoExterno && scope.kind === 'phase';
  const escopoLoja = escopoExterno && scope.kind === 'store';
  const phaseId = escopoFase ? scope.phase_id : null;

  const [clients, setClients] = useState([]);
  const [targetLocal, setTarget] = useState(''); // '' | TARGET_DEFAULT | subdomain
  // Com escopo externo o alvo é a empresa dele: 'ongoing' e 'phase' gravam em níveis diferentes,
  // mas os dois pertencem à mesma empresa.
  const target = escopoExterno ? scope.subdomain : targetLocal;
  const [msg, setMsg] = useState(null); // {type, text}
  const [loading, setLoading] = useState(false);

  // lista de configs (modo cliente)
  const [configs, setConfigs] = useState([]);
  // edição
  const [editing, setEditing] = useState(null); // null | {id?, name}
  const [preview, setPreview] = useState(null); // {default, override, merged, overridden_paths}
  const [temaOverride, setTemaOverride] = useState({});
  const [brandingOverride, setBrandingOverride] = useState(null); // {logoBucket, logoKey} | null
  const [localLogoPreview, setLocalLogoPreview] = useState(null); // preview imediato do arquivo enviado
  const [savedLogoUrl, setSavedLogoUrl] = useState(null); // URL presignada do logo já salvo
  const [otherJson, setOtherJson] = useState('{}');
  const [activateOnCreate, setActivateOnCreate] = useState(true);

  const isDefault = target === TARGET_DEFAULT;
  // Refs porque o efeito acima roda antes das funções serem declaradas neste corpo.
  const resetEditorRef = useRef(null);
  const loadConfigsRef = useRef(null);
  const flash = (type, text) => { setMsg({ type, text }); if (type === 'success') setTimeout(() => setMsg(null), 4000); };

  // ---- carregamento ----
  useEffect(() => {
    (async () => {
      try {
        const data = await apiService.sduiListClients(userId);
        setClients(data.clients || []);
      } catch (e) {
        flash('error', `Erro ao carregar clientes: ${e.response?.data?.error || e.message}`);
      }
    })();
  }, [userId]);

  // Com escopo externo, trocar de escopo recarrega a lista: sem isto a tela mostraria a config do
  // escopo anterior e o CS editaria o alvo errado.
  useEffect(() => {
    if (!escopoExterno || escopoLoja) return;
    resetEditorRef.current && resetEditorRef.current();
    loadConfigsRef.current && loadConfigsRef.current(scope.subdomain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [escopoExterno, escopoLoja, escopoFase, phaseId, scope?.subdomain]);

  const resetEditor = () => { setEditing(null); setPreview(null); setTemaOverride({}); setBrandingOverride(null); setLocalLogoPreview(null); setSavedLogoUrl(null); setOtherJson('{}'); };

  const loadConfigs = useCallback(async (subdomain) => {
    setLoading(true);
    try {
      // No escopo de fase a lista vem de phase_sdui_configs (uma ativa por fase).
      const data = escopoFase
        ? await apiService.sduiListPhaseConfigs(userId, subdomain, phaseId)
        : await apiService.sduiListConfigs(userId, subdomain);
      setConfigs(data.configs || []);
    } catch (e) {
      flash('error', `Erro ao listar configurações: ${e.response?.data?.error || e.message}`);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, escopoFase, phaseId]);

  resetEditorRef.current = resetEditor;
  loadConfigsRef.current = loadConfigs;

  // monta o editor a partir de um override (parcial) ou de uma config completa (default)
  // logoUrl = URL presignada (de exibição) do logo já salvo, vinda da API
  const fillEditor = (defaultCfg, override, overriddenPaths, logoUrl) => {
    const ov = override || {};
    setTemaOverride(ov.tema && typeof ov.tema === 'object' ? { ...ov.tema } : {});
    setBrandingOverride(ov.branding && typeof ov.branding === 'object' ? { ...ov.branding } : null);
    const rest = { ...ov };
    delete rest.tema; delete rest.branding;
    setOtherJson(JSON.stringify(rest, null, 2));
    setPreview({ default: defaultCfg || {}, override: ov, overridden_paths: overriddenPaths || [] });
    setSavedLogoUrl(logoUrl || null);
    setLocalLogoPreview(null);
  };

  const onSelectTarget = async (value) => {
    setTarget(value);
    resetEditor();
    setConfigs([]);
    if (!value) return;
    if (value === TARGET_DEFAULT) {
      setLoading(true);
      try {
        const d = await apiService.sduiGetDefault(userId); // {id, name, config, logo_url}
        const cfg = d.config || {};
        setEditing({ name: d.name || 'Padrão Global' });
        // no default, "override" É a config completa; usamos o próprio cfg como base
        // para que o form de tema liste todos os campos.
        fillEditor(cfg, cfg, [], d.logo_url);
      } catch (e) {
        flash('error', `Erro ao carregar default: ${e.response?.data?.error || e.message}`);
      } finally {
        setLoading(false);
      }
    } else {
      await loadConfigs(value);
    }
  };

  const openConfigEditor = async (cfg /* {id,name} | null */) => {
    setLoading(true);
    try {
      const data = escopoFase
        ? await apiService.sduiPhasePreview(userId, target, phaseId, cfg ? cfg.id : undefined)
        : await apiService.sduiPreview(userId, target, cfg ? cfg.id : undefined);
      // O preview de FASE devolve `phase_override`/`overridden_paths_phase` (tem também o nível de
      // empresa, `company_override`), não `override`/`overridden_paths` como o de empresa — ler os
      // nomes genéricos aqui sempre dava `undefined`, e fillEditor tratava isso como "sem conteúdo"
      // (ver PND-GLOBAL-22: editar uma fase sempre abria o form vazio, e Salvar sobrescrevia a
      // config real com esse vazio).
      const override = escopoFase ? data.phase_override : data.override;
      const overriddenPaths = escopoFase ? data.overridden_paths_phase : data.overridden_paths;
      const mergedLogoUrl = data.merged?.branding?.logoUrl || null;
      if (cfg) {
        setEditing({ id: cfg.id, name: cfg.name, active: cfg.active });
        fillEditor(data.default, override, overriddenPaths, mergedLogoUrl);
      } else {
        setEditing({ name: '' }); // nova
        fillEditor(data.default, {}, [], mergedLogoUrl);
      }
    } catch (e) {
      flash('error', `Erro ao abrir editor: ${e.response?.data?.error || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ---- ações de tema ----
  const temaKeys = preview ? Object.keys((preview.default && preview.default.tema) || {}) : [];
  // garante que campos só presentes no override também apareçam
  Object.keys(temaOverride).forEach(k => { if (!temaKeys.includes(k)) temaKeys.push(k); });

  const temaValue = (k) => (temaOverride[k] !== undefined ? temaOverride[k]
    : (preview?.default?.tema ? preview.default.tema[k] : ''));
  const isSpecific = (k) => temaOverride[k] !== undefined;

  const setTema = (k, v) => setTemaOverride(prev => ({ ...prev, [k]: v }));
  const inheritTema = (k) => setTemaOverride(prev => { const n = { ...prev }; delete n[k]; return n; });

  // ---- logo ----
  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'image/png') { flash('error', 'O logo deve ser um PNG'); return; }
    setLoading(true);
    try {
      const sub = isDefault ? 'admin' : target;
      const res = await apiService.sduiUploadLogo({ userId, subdomain: sub, file, scope: isDefault ? 'default' : undefined });
      setBrandingOverride({ logoBucket: res.logoBucket, logoKey: res.logoKey });
      // preview imediato via data URL (a CSP do servidor permite data:, mas não blob:)
      const reader = new FileReader();
      reader.onload = () => setLocalLogoPreview(reader.result);
      reader.readAsDataURL(file);
      flash('success', 'Logo enviado. Salve a configuração para aplicar.');
    } catch (e2) {
      flash('error', `Erro no upload do logo: ${e2.response?.data?.error || e2.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ---- montar override / salvar ----
  const buildOverride = () => {
    let rest;
    try {
      rest = JSON.parse(otherJson || '{}');
    } catch (e) {
      throw new Error('JSON dos demais nós é inválido');
    }
    if (typeof rest !== 'object' || Array.isArray(rest)) throw new Error('JSON dos demais nós deve ser um objeto');
    const ov = { ...rest };
    if (Object.keys(temaOverride).length) ov.tema = temaOverride;
    if (brandingOverride) ov.branding = brandingOverride;
    return ov;
  };

  // Sobe um JSON completo: substitui tema + nós estruturais no editor (mantém o logo).
  // Carrega pra revisão — só persiste quando clicar Salvar.
  const onUploadFullJson = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const cfg = JSON.parse(text);
      if (typeof cfg !== 'object' || Array.isArray(cfg)) throw new Error('O JSON deve ser um objeto');
      setTemaOverride(cfg.tema && typeof cfg.tema === 'object' ? { ...cfg.tema } : {});
      const rest = { ...cfg };
      delete rest.tema; delete rest.branding; // tema vai pro form; logo é mantido à parte
      setOtherJson(JSON.stringify(rest, null, 2));
      flash('success', 'JSON carregado no editor — revise e clique em Salvar. O logo atual foi mantido.');
    } catch (err) {
      flash('error', `JSON inválido: ${err.message}`);
    }
  };

  const onSave = async () => {
    if (!editing) return;
    setLoading(true);
    try {
      const ov = buildOverride();
      if (isDefault) {
        await apiService.sduiSaveDefault({ userId, name: editing.name || 'Padrão Global', config: ov });
        flash('success', 'Default global salvo.');
      } else if (editing.id) {
        if (escopoFase) {
          // Editar não é publicar: preserva o `active` que a config já tinha, em vez de forçar
          // `true` (ver PND-GLOBAL-22 — isso fazia qualquer edição de uma fase inativa ativá-la
          // sem aviso, e combinado com o bug do form abrindo vazio, publicava um config vazio).
          await apiService.sduiSavePhaseConfig({ userId, subdomain: target, phaseId, id: editing.id, name: editing.name, config: ov, activate: !!editing.active });
        } else {
          await apiService.sduiUpdateConfig({ userId, subdomain: target, id: editing.id, name: editing.name, config: ov });
        }
        flash('success', 'Configuração atualizada.');
        await loadConfigs(target);
      } else {
        if (!editing.name?.trim()) { flash('error', 'Dê um nome à configuração'); setLoading(false); return; }
        if (escopoFase) {
          await apiService.sduiSavePhaseConfig({ userId, subdomain: target, phaseId, name: editing.name.trim(), config: ov, activate: activateOnCreate });
        } else {
          await apiService.sduiCreateConfig({ userId, subdomain: target, name: editing.name.trim(), config: ov, activate: activateOnCreate });
        }
        flash('success', 'Configuração criada.');
        await loadConfigs(target);
        resetEditor();
      }
    } catch (e) {
      flash('error', e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const onActivate = async (cfg) => {
    setLoading(true);
    try {
      // escopoFase bate em outra tabela (phase_sdui_configs, não sdui_configs) — faltava este
      // branch (onSave/onDelete já têm o deles), e não existia rota de ativar fase até esta
      // correção. Ver PND-GLOBAL-22.
      if (escopoFase) {
        await apiService.sduiActivatePhaseConfig({ userId, subdomain: target, id: cfg.id });
      } else {
        await apiService.sduiActivateConfig({ userId, subdomain: target, id: cfg.id });
      }
      flash('success', `"${cfg.name}" ativada.`);
      await loadConfigs(target);
    } catch (e) {
      flash('error', e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };

  const onDelete = async (cfg) => {
    if (!(await confirmar(`Remover a configuração "${cfg.name}"?`, { titulo: 'Remover configuração', perigo: true, rotuloConfirmar: 'Remover' }))) return;
    setLoading(true);
    try {
      if (escopoFase) await apiService.sduiDeletePhaseConfig({ userId, subdomain: target, id: cfg.id });
      else await apiService.sduiDeleteConfig({ userId, subdomain: target, id: cfg.id });
      flash('success', 'Configuração removida.');
      if (editing?.id === cfg.id) resetEditor();
      await loadConfigs(target);
    } catch (e) {
      flash('error', e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };

  // Baixa o JSON do SDUI de UMA configuração, sem precisar abrir o editor.
  //
  // Baixa o `merged`, não o `override`: é o que o app de fato RECEBE (default global +
  // override, já fundidos pelo /sdui/get-config-enterprise). O override sozinho não descreve
  // a tela, porque boa parte dela vem do default — comparar dois ambientes só pelo override
  // esconde divergência que está no padrão global.
  const onDownloadJson = async (cfg) => {
    setLoading(true);
    try {
      const data = escopoFase
        ? await apiService.sduiPhasePreview(userId, target, phaseId, cfg.id)
        : await apiService.sduiPreview(userId, target, cfg.id);
      const json = JSON.stringify(data.merged || {}, null, 2);
      const limpo = String(cfg.name || 'config').replace(/[^\w.-]+/g, '_');
      const nome = `sdui-${target}-${limpo}-${new Date().toISOString().slice(0, 10)}.json`;
      // data: URL, não blob: — a CSP do servidor (node/server.js) não lista `blob:` em
      // diretiva nenhuma, e o mesmo cuidado já vale para o preview do logo aqui em cima.
      const a = document.createElement('a');
      a.href = `data:application/json;charset=utf-8,${encodeURIComponent(json)}`;
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      flash('error', `Erro ao baixar JSON: ${e.response?.data?.error || e.message}`);
    } finally { setLoading(false); }
  };

  const logoUrl = localLogoPreview || savedLogoUrl;

  // JSON de referência (default) — mesmos nós do override (sem tema/branding), só leitura
  const referenceJson = (() => {
    const r = { ...(preview?.default || {}) };
    delete r.tema; delete r.branding;
    return JSON.stringify(r, null, 2);
  })();

  return (
    <div className="wl-container">
      {!escopoExterno && <h1 className="wl-title">🎨 Configurações Whitelabel</h1>}
      {!escopoExterno && (
        <p className="wl-sub">Configuração server-driven do app por cliente, com herança do padrão global (default core).</p>
      )}

      {msg && <div className={`wl-alert wl-alert-${msg.type}`}>{msg.text}</div>}

      {/* Whitelabel NÃO tem nível de loja: default global -> empresa -> fase. Dizer isso é melhor
          que mostrar campos que não gravariam em lugar nenhum. */}
      {escopoLoja && (
        <div className="wl-alert wl-alert-info">
          Whitelabel não tem configuração por <strong>loja</strong> — os níveis são padrão global,
          empresa e fase. Escolha <em>Padrão da empresa</em> ou uma <em>fase</em> no seletor acima.
        </div>
      )}

      {escopoFase && (
        <p className="wl-sub">
          Editando o whitelabel <strong>desta fase</strong>. O que você salvar é um <em>delta</em>
          {' '}sobre o que a empresa e o padrão global já definem, e vale enquanto o vendedor estiver nela.
        </p>
      )}

      {!escopoExterno && (
      <div className="wl-row">
        <label className="wl-label">Cliente:</label>
        <select className="wl-select" value={target} onChange={e => onSelectTarget(e.target.value)}>
          <option value="">-- Selecione --</option>
          <option value={TARGET_DEFAULT}>⭐ Padrão Global (default de todos)</option>
          {clients.map(c => <option key={c.subdomain} value={c.subdomain}>{c.name} ({c.subdomain})</option>)}
        </select>
        {loading && <span className="wl-loading">carregando…</span>}
      </div>
      )}

      {/* MODO CLIENTE: lista de configs */}
      {target && !isDefault && !escopoLoja && (
        <div className="wl-card">
          <div className="wl-card-head">
            <h2>Configurações de {target}</h2>
            <button className="wl-btn wl-btn-primary" onClick={() => openConfigEditor(null)}>+ Nova configuração</button>
          </div>
          {configs.length === 0 ? (
            <p className="wl-empty">Nenhuma configuração específica. Vale o padrão global.</p>
          ) : (
            <table className="wl-table">
              <thead><tr><th>Nome</th><th>Status</th><th>Atualizada</th><th>Ações</th></tr></thead>
              <tbody>
                {configs.map(c => (
                  <tr key={c.id} className={editing?.id === c.id ? 'wl-tr-editing' : ''}>
                    <td>{c.name}</td>
                    <td>{c.active ? <span className="wl-badge wl-badge-active">ativa</span> : <span className="wl-badge">inativa</span>}</td>
                    <td>{(c.updated || c.created || '').replace('T', ' ').slice(0, 16)}</td>
                    <td className="wl-actions">
                      {!c.active && <button className="wl-btn" onClick={() => onActivate(c)}>Ativar</button>}
                      <button className="wl-btn" onClick={() => openConfigEditor(c)}>Editar</button>
                      <button className="wl-btn" title="Baixa o JSON que o app recebe (default + override já fundidos)"
                              onClick={() => onDownloadJson(c)}>Baixar JSON</button>
                      <button className="wl-btn wl-btn-danger" onClick={() => onDelete(c)}>Remover</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* EDITOR (default global ou config do cliente) */}
      {editing && preview && (
        <div className="wl-card">
          <div className="wl-card-head">
            <h2>{isDefault ? 'Editar Padrão Global' : (editing.id ? `Editar: ${editing.name}` : 'Nova configuração')}</h2>
            {isDefault && (
              <label className="wl-btn" title="Substitui tema + nós estruturais pelo arquivo (mantém o logo). Revise e salve.">
                ⬆️ Subir JSON completo
                <input type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={onUploadFullJson} />
              </label>
            )}
          </div>

          {!isDefault && (
            <div className="wl-row">
              <label className="wl-label">Nome:</label>
              <input className="wl-input" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="ex: Campanha Natal" />
              {!editing.id && (
                <label className="wl-check"><input type="checkbox" checked={activateOnCreate} onChange={e => setActivateOnCreate(e.target.checked)} /> ativar ao criar</label>
              )}
            </div>
          )}

          {!isDefault && (
            <p className="wl-legend">
              <span className="wl-badge wl-badge-spec">específico</span> = sobrescreve o padrão ·
              <span className="wl-badge wl-badge-def">default</span> = herdado do padrão global
            </p>
          )}

          {/* TEMA */}
          <h3 className="wl-h3">Tema</h3>
          <div className="wl-tema-grid">
            {temaKeys.map(k => {
              const val = temaValue(k);
              const kind = temaFieldKind(k, val);
              const spec = isSpecific(k);
              return (
                <div key={k} className="wl-field">
                  <div className="wl-field-head">
                    <span className="wl-field-name">{k}</span>
                    {!isDefault && (spec
                      ? <span className="wl-badge wl-badge-spec">específico</span>
                      : <span className="wl-badge wl-badge-def">default</span>)}
                  </div>
                  <div className="wl-field-input">
                    {kind === 'color' && <input type="color" value={/^#/.test(val) ? val : '#000000'} onChange={e => setTema(k, e.target.value)} />}
                    <input
                      type={kind === 'number' ? 'number' : 'text'}
                      value={val ?? ''}
                      onChange={e => setTema(k, kind === 'number' ? Number(e.target.value) : e.target.value)}
                    />
                    {!isDefault && spec && <button className="wl-link" title="herdar default" onClick={() => inheritTema(k)}>herdar</button>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* LOGO */}
          <h3 className="wl-h3">Logo (PNG)</h3>
          <div className="wl-logo">
            {logoUrl ? <img src={logoUrl} alt="logo" className="wl-logo-img" /> : <div className="wl-logo-empty">sem logo</div>}
            <div>
              <input type="file" accept="image/png" onChange={onLogoChange} />
              {brandingOverride?.logoKey && <p className="wl-mono">novo: {brandingOverride.logoKey}</p>}
            </div>
          </div>

          {/* DEMAIS NÓS */}
          <h3 className="wl-h3">Demais nós (navigation, screens, sheets…) — {isDefault ? 'config' : 'override'} em JSON</h3>
          {isDefault ? (
            <textarea className="wl-json" spellCheck={false} value={otherJson} onChange={e => setOtherJson(e.target.value)} rows={18} />
          ) : (
            <div className="wl-json-cols">
              <div className="wl-json-col">
                <div className="wl-json-label">Seu override — edite só o nó/campo que quiser sobrescrever</div>
                <textarea className="wl-json" spellCheck={false} value={otherJson} onChange={e => setOtherJson(e.target.value)} rows={22} />
              </div>
              <div className="wl-json-col">
                <div className="wl-json-label">Referência: default global (somente leitura)</div>
                <textarea className="wl-json" spellCheck={false} readOnly value={referenceJson} rows={22} />
              </div>
            </div>
          )}

          <div className="wl-save-row">
            <button className="wl-btn wl-btn-primary" disabled={loading} onClick={onSave}>{loading ? 'Salvando…' : 'Salvar'}</button>
            {!isDefault && <button className="wl-btn" onClick={resetEditor}>Cancelar</button>}
          </div>
        </div>
      )}
    </div>
  );
}
